<template>
    <div>
        <div class="group-select__control" :class="{ 'group-select__control--error': hasError }">
            <e-taglist>
                <e-tag
                    v-for="group in visibleGroupChips"
                    :key="group.id"
                    size="is-small"
                    closable
                    :tooltip="group.name"
                    aria-close-label="Close tag"
                    close-icon="times"
                    @close="remove(group)"
                >
                    {{ group.label }}
                </e-tag>

                <Button
                    v-if="overflowCount > 0"
                    variant="text"
                    size="small"
                    :label="`+${overflowCount} more`"
                    @click="openModal = true"
                />

                <Button
                    v-if="!atLimit || showLimitHint"
                    v-tooltip.top="atLimit ? `Maximum of ${limit} groups reached` : undefined"
                    :class="{ 'disabled-link': disabled }"
                    :disabled="disabled || atLimit"
                    variant="text"
                    icon="pi pi-plus-circle"
                    size="small"
                    @click="openModal = true"
                />
            </e-taglist>
        </div>

        <p v-if="showLimitHint" class="tw:text-xs tw:text-muted-color tw:mt-1">
            {{ limitHint }}
        </p>

        <modal-select
            ref="modalSelect"
            label="group"
            :open="openModal"
            :limit="limit"
            modal-width="52rem"
            body-min-height="40vh"
            body-max-height="60vh"
            :filters="filters"
            :sorts="sorts"
            endpoint="group-selector-search"
            :available-view="['detail']"
            :already-selected="alreadySelected"
            :additional-include="['category']"
            base-view="detail"
            @chosen="save"
            @close="close"
        >
            <template #detail-view="{ items, selected, selectItem, selectAllState, selectAllDisabled, selectAllDisabledReason, isRowCheckable }">
                <div class="column is-full is-narrow has-text-left group-select-table">
                    <e-table
                        ref="table"
                        :checked-rows="selected"
                        :data="items"
                        checkable
                        :is-row-checkable="isRowCheckable"
                        :focusable="limit === 1"
                        :header-checkable="limit !== 1"
                        :header-checkbox-state="selectAllState"
                        :header-checkbox-disabled="selectAllDisabled"
                        :header-checkbox-disabled-tooltip="selectAllDisabledReason"
                        backend-sorting
                        @check="(selected, row) => selectItem(row)"
                        @select="selectItem"
                        @sort="onSort"
                    >
                        <e-table-column v-slot="{ row: group }" ref="name" field="name" label="Name" sortable>
                            <e-tooltip :label="group.name" multilined class="tw:block tw:min-w-0 tw:truncate groups-select-table-text">
                                {{ group.name }}
                            </e-tooltip>
                        </e-table-column>

                        <e-table-column
                            v-slot="{ row: group }"
                            ref="category"
                            field="category_name"
                            label="Category"
                            sortable
                        >
                            <e-tooltip
                                v-if="group.category"
                                :label="group.category.name"
                                multilined
                                class="tw:block tw:min-w-0 tw:truncate groups-select-table-text"
                            >
                                {{ group.category.name }}
                            </e-tooltip>
                        </e-table-column>
                    </e-table>
                </div>
            </template>
        </modal-select>
    </div>
</template>
<script>
import ModalSelect from '@/components/modal-select/base/ModalSelect.vue';
import GroupCategoryFilter from '@/components/modal-select/filters/GroupCategoryFilter.js';
import TextFilter from '@/components/modal-select/filters/TextFilter.js';
import QueryParamSyncForModalSelectMixin from '@/mixins/QueryParamSyncForModalSelectMixin.js';
import GroupCategory from '@/models/GroupCategory.js';
import { formatGroupChipLabel as formatGroupCategoryChipLabel } from '@/utilities/groupCategoryLabels.js';

let categoryFilterOptionsCache = null;
let categoryFilterOptionsRequest = null;

export default {
    name: 'GroupSelect',
    components: { ModalSelect },

    mixins: [QueryParamSyncForModalSelectMixin],

    props: {
        open: {
            type: Boolean,
            required: false,
            default: false,
        },

        label: {
            type: String,
            required: false,
            default: 'Groups',
        },

        alreadySelected: {
            type: Array,
            required: false,
            default: () => [],
        },

        limit: {
            type: Number,
            required: false,
            default: 100,
        },

        hasError: {
            type: Boolean,
            required: false,
            default: false,
        },

        enableQueryParams: {
            type: Boolean,
            required: false,
            default: false,
        },

        urlParam: {
            type: String,
            required: false,
            default: '',
        },

        endpoint: {
            type: String,
            required: false,
            default: 'groups',
        },

        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },

        maxVisibleTags: {
            type: Number,
            required: false,
            default: null,
        },

        showLimitHint: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    emits: ['save', 'close', 'remove'],

    data: () => ({
        openModal: false,
        filters: [new TextFilter('name'), new GroupCategoryFilter('category_id', 'Category')],
        sorts: ['name'],
        selectedFromQuery: null,
        selectedRows: [],
        hasLoadedCategoryFilterOptions: false,
    }),

    computed: {
        selectedGroups() {
            return this.alreadySelected.length > 0 ? this.alreadySelected : this.selectedFromQuery;
        },

        visibleGroups() {
            const groups = this.selectedGroups || [];

            if (!this.maxVisibleTags || groups.length <= this.maxVisibleTags) {
                return groups;
            }

            return groups.slice(0, this.maxVisibleTags);
        },

        visibleGroupChips() {
            return (this.visibleGroups || []).map(group => ({
                ...group,
                label: this.formatGroupChipLabel(group),
            }));
        },

        overflowCount() {
            const groups = this.selectedGroups || [];

            if (!this.maxVisibleTags) {
                return 0;
            }

            return Math.max(0, groups.length - this.maxVisibleTags);
        },

        atLimit() {
            return this.alreadySelected.length >= this.limit;
        },

        limitHint() {
            return `You can link up to ${this.limit} ${this.limit === 1 ? 'group' : 'groups'}.`;
        },
    },

    watch: {
        open(open) {
            this.openModal = open;
        },

        openModal(isOpen) {
            if (isOpen) {
                this.loadCategoryFilterOptions();
            }
        },
    },

    created() {
        if (this.enableQueryParams) {
            this.fillFromQueryParams();
        }
    },

    beforeUnmount() {
        this.openModal = false;
    },

    methods: {
        async fetchCategoryFilterOptions() {
            if (Array.isArray(categoryFilterOptionsCache)) {
                return categoryFilterOptionsCache;
            }

            if (!categoryFilterOptionsRequest) {
                categoryFilterOptionsRequest = GroupCategory.api()
                    .get('/api/group-categories?sort=name&per_page=1000')
                    .then(response => {
                        const categories = response?.response?.data?.data;

                        if (!Array.isArray(categories)) {
                            return null;
                        }

                        return [
                            { value: '', label: 'Clear Filter' },
                            ...categories
                                .filter(category => category?.id && category?.name)
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map(category => ({ value: category.id, label: category.name })),
                        ];
                    })
                    .catch(error => {
                        console.warn('Failed to load group categories for GroupSelect filter:', error);
                        return null;
                    })
                    .finally(() => {
                        categoryFilterOptionsRequest = null;
                    });
            }

            const options = await categoryFilterOptionsRequest;

            if (Array.isArray(options)) {
                categoryFilterOptionsCache = options;
            }

            return options;
        },

        async loadCategoryFilterOptions() {
            if (this.hasLoadedCategoryFilterOptions) {
                return;
            }

            try {
                const options = await this.fetchCategoryFilterOptions();

                if (!Array.isArray(options)) {
                    return;
                }

                const categoryFilter = this.filters.find(filter => filter.field === 'category_id');

                if (!categoryFilter) {
                    return;
                }

                categoryFilter.options = [];

                options.forEach(option => {
                    categoryFilter.addOption(option.value, option.label);
                });

                this.hasLoadedCategoryFilterOptions = true;
            } catch (error) {
                console.warn('Failed to load group categories for GroupSelect filter:', error);
            }
        },

        save(chosen) {
            if (this.enableQueryParams) {
                this.setQueryParams(chosen);
            }
            this.$emit('save', chosen);
        },

        close() {
            this.openModal = false;

            this.$emit('close');
        },

        remove(chosen) {
            const group = this.selectedGroups.find(selected => selected.id === chosen.id) || chosen;

            this.$emit('remove', group);
            this.selectedFromQuery = [];
            this.removeQueryParams();
        },

        onSort(field, order) {
            this.$refs.modalSelect.onSort(field, order);
        },

        formatGroupChipLabel(group) {
            return formatGroupCategoryChipLabel(group);
        },
    },
};
</script>

<style lang="scss" scoped>
@use './select-control' as *;

@include modal-select-control('group-select');

/* Scroll the table independently so Name/Category headers stay sticky */
.group-select-table {
    max-height: min(40vh, 28rem);
    overflow: auto;

    :deep(.p-datatable-table-container) {
        overflow: visible;
    }

    :deep(.p-datatable-thead > tr > th) {
        position: sticky;
        top: 0;
        z-index: 2;
        background: var(--p-content-background);
    }
}
</style>